import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';


interface ScoreRadarChartProps {
  scores: Record<string, number>;
  criteria: Array<{
    id: string;
    name: string;
    description: string;
    category: string;
  }>;
  overall: number;
}

export const ScoreRadarChart = ({ scores, criteria, overall }: ScoreRadarChartProps) => {
  // Fonction pour diviser les textes longs sur 2 lignes
  const formatLabelForRadar = (text: string) => {
    if (text.length <= 15) return text;

    // Chercher un espace proche du milieu pour couper proprement
    const middle = Math.floor(text.length / 2);
    let splitIndex = text.lastIndexOf(' ', middle + 5);

    if (splitIndex === -1 || splitIndex < middle - 5) {
      splitIndex = text.indexOf(' ', middle - 5);
    }

    if (splitIndex === -1) {
      // Pas d'espace trouvé, couper au milieu
      return text.substring(0, middle) + '\n' + text.substring(middle);
    }

    return text.substring(0, splitIndex) + '\n' + text.substring(splitIndex + 1);
  };

  const chartData = criteria.map((criterion) => {
    // Utiliser les vraies compétences configurées par le recruteur
    return {
      subject: formatLabelForRadar(criterion.name), // Formaté pour 2 lignes si nécessaire
      fullName: criterion.name,
      value: scores[criterion.id] || 0,
    };
  });

  const getBarColor = (score: number) => {
    if (score >= 7.5) return '#22C55E'; // green-500
    if (score >= 5.0) return '#EAB308'; // yellow-500  
    return '#EF4444'; // red-500
  };

  const getBarWidth = (score: number) => {
    return Math.max((score / 10) * 176, 8); // 176px = w-44, minimum 8px pour visibilité
  };

  return (
    <div className="w-full rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
      <div className="w-full p-4 bg-gray-50 border-b border-neutral-400/20 inline-flex justify-start items-center gap-2.5">
        <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">Évaluation des compétences</div>
      </div>

      <div className="w-full h-[490px] px-4 py-5 relative inline-flex justify-start items-center gap-24">
        {/* Graphique radar fonctionnel */}
        <div className="flex-1 max-w-[50%] h-[450px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 40, right: 80, bottom: 40, left: 80 }}>
              <PolarGrid stroke="#E9EAEB" />
              <PolarAngleAxis
                dataKey="subject"
                tick={{
                  fontSize: 11,
                  fill: '#9CA3AF',
                  fontFamily: 'Bricolage Grotesque',
                  textAnchor: 'middle'
                }}
                tickFormatter={(value) => value}
              />
              <PolarRadiusAxis
                domain={[0, 10]}
                tick={{
                  fontSize: 10,
                  fill: '#9CA3AF',
                  fontFamily: 'Bricolage Grotesque'
                }}
                tickCount={6}
              />
              <Radar
                dataKey="value"
                stroke="#40E0D0"
                fill="#40E0D0"
                fillOpacity={0.1}
                strokeWidth={2}
                dot={{ fill: '#40E0D0', strokeWidth: 2, stroke: 'white', r: 4 }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Liste des compétences */}
        <div className="flex-1 max-w-[50%] inline-flex flex-col justify-center items-start gap-8">
          <div className="self-stretch flex flex-col justify-start items-start gap-4">
            {chartData.map((item, index) => (
              <div key={index} className="self-stretch inline-flex justify-between items-center">
                <div className="justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">{item.fullName}</div>
                <div className="flex justify-start items-center gap-4">
                  <div className="w-44 h-2 bg-gray-50 rounded-full inline-flex flex-col justify-start items-start gap-2.5">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${getBarWidth(item.value)}px`,
                        backgroundColor: getBarColor(item.value)
                      }}
                    />
                  </div>
                  <div className="w-5 justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">{item.value.toFixed(1)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Moyenne générale */}
          <div className="self-stretch p-5 bg-yellow-50 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-yellow-400 flex flex-col justify-start items-start gap-1">
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">Moyenne générale</div>
              <div className="justify-start text-yellow-400 text-2xl font-semibold font-['Bricolage_Grotesque']">{overall.toFixed(1)}/10</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};